'use client'

import { cx, useImageProvider } from '@7f-ui'
import { shopTestId } from '@utils/utils-shop-test-ids'
import Image from 'next/image'
import Link from 'next/link'
import type { ActivityListFragment } from 'src/lib/backend/documents/activities'

type AppActivityCardProps = {
  activity: ActivityListFragment
  activityName: string
  experienceCountLabel: string
}

export function AppActivityCard({
  activity,
  activityName,
  experienceCountLabel,
}: AppActivityCardProps) {
  const images = useImageProvider()
  const href = `/aktivitaeten/${activity.slug}/`
  const available = activity.experienceCount > 0

  return (
    <div className="space-y-4" data-testid={shopTestId('ActivityCard.root')}>
      {available ? (
        <Link href={href} data-testid={shopTestId('ActivityCard.link')}>
          <CardContent
            activity={activity}
            activityName={activityName}
            available={available}
            experienceCountLabel={experienceCountLabel}
            fallbackImageUrl={images.activities.list}
          />
        </Link>
      ) : (
        <CardContent
          activity={activity}
          activityName={activityName}
          available={available}
          experienceCountLabel={experienceCountLabel}
          fallbackImageUrl={images.activities.list}
        />
      )}

      {activity.description ? <p className="mt-3">{activity.description}</p> : null}
    </div>
  )
}

function CardContent({
  activity,
  activityName,
  available,
  experienceCountLabel,
  fallbackImageUrl,
}: {
  activity: ActivityListFragment
  activityName: string
  available: boolean
  experienceCountLabel: string
  fallbackImageUrl: string
}) {
  return (
    <div
      className={cx('group relative isolate flex aspect-square overflow-hidden rounded-2xl p-6')}
    >
      <Image
        className={cx(
          'absolute top-0 left-0 -z-20 h-full w-full object-cover',
          available ? 'opacity-100' : 'opacity-40'
        )}
        src={activity.headerImage?.url ?? fallbackImageUrl}
        alt={activity.headerImage?.alt ?? activityName}
        sizes="(min-width: 768px) 50vw, (min-width: 1440px) 70vw, 500px"
        fill
      />

      {available ? (
        <div className="absolute top-0 left-0 -z-10 size-full group-hover:bg-primary/10 group-active:bg-primary/15" />
      ) : null}

      <h3
        className={cx('flex min-w-0 flex-col justify-end text-white', !available && 'text-primary')}
      >
        <span className="hyphens-auto font-heading3">{activityName}</span>
        <span className="font-body-small">{experienceCountLabel}</span>
      </h3>
    </div>
  )
}
